import { Asset, AssetStatus } from '../../types';

/**
 * Whether an asset has left the estate, and how.
 *
 * Mirrors wzee-be's src/asset/asset-disposal.ts. The backend decides the
 * totals; this decides what the disposed view lists, and the two have to agree
 * or the list will not add up to the figure beside it. Keep them in step.
 */

/** The flags that mean the item is gone, paired with how it left. */
const DISPOSAL_REASONS: Array<[keyof Asset, string]> = [
  ['distributed', 'Distributed'],
  ['sold', 'Sold'],
  ['donate', 'Donated'],
  ['givenToFriend', 'Given to a friend'],
  ['thrownOut', 'Thrown out'],
];

/**
 * Distributed, sold, donated, given away or thrown out, or marked distributed
 * or closed.
 */
export function isAssetDisposed(asset: Asset): boolean {
  if (!asset) return false;

  if (DISPOSAL_REASONS.some(([flag]) => Boolean(asset[flag]))) return true;

  return asset.status === AssetStatus.DISTRIBUTED || asset.status === AssetStatus.CLOSED;
}

/**
 * How the item left, in the order the flags are recorded. Several can be true
 * at once, so this reports what was recorded rather than picking one.
 */
export function assetDisposalReasons(asset: Asset): string[] {
  if (!asset) return [];

  const reasons = DISPOSAL_REASONS.filter(([flag]) => Boolean(asset[flag])).map(
    ([, label]) => label,
  );

  if (asset.status === AssetStatus.DISTRIBUTED && !reasons.includes('Distributed')) {
    reasons.push('Distributed');
  }
  if (asset.status === AssetStatus.CLOSED) {
    reasons.push('Closed');
  }

  return reasons;
}
